import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import { Box, Link, Skeleton, Typography } from '@mui/material';

import { FieldRow } from '~/components/common/detailFields';
import { HeaderField } from '~/components/common/HeaderField';
import type { CarInProperty } from '~/data/schemas/car';
import { callbackTypeValues, type EntryTypeKey } from '~/data/schemas/ticket';
import { useLocaleTerms } from '~/hooks/useLocaleTerms';
import { FULL_MAINTENANCE_COMPLETION } from '~/lib/kpi/contractTargets';
import {
  formatDecimal,
  formatDecimalTwo,
  formatNumber,
  formatPercent,
  formatSecondsToDays,
} from '~/utils/format';
import { buildUrlForPropertyTickets } from '~/utils/urlBuilder';

/** A KPI whose value is the tickets behind it — the number links to them, and carries the grid's pass/fail mark where the KPI has a target. */
function KpiField({
  label,
  loaded,
  value,
  href,
  isGood,
}: {
  label: string;
  loaded: boolean;
  value: string | null;
  href: string;
  isGood?: boolean;
}) {
  return (
    <HeaderField label={label}>
      {!loaded ? (
        <Skeleton width={70} animation="wave" />
      ) : value === null ? (
        <Typography variant="body2">—</Typography>
      ) : (
        <Box display="flex" alignItems="center" gap={0.5}>
          {isGood === undefined ? null : isGood ? (
            <CheckCircleIcon sx={{ fontSize: 16 }} color="success" />
          ) : (
            <WarningIcon sx={{ fontSize: 16, color: '#ED6C02' }} />
          )}
          <Link sx={{ color: 'inherit', textDecorationColor: 'inherit' }} href={href}>
            <Typography variant="body2">{value}</Typography>
          </Link>
        </Box>
      )}
    </HeaderField>
  );
}

/**
 * How the car is doing — the Performance card on Service & Performance.
 *
 * Every ticket-derived column of the property's Elevators tab, over the app's current
 * date window: the KPIs come from that tab's own read, so these are the numbers on the
 * row that was clicked. This card is why the page keeps the Period control when the
 * other detail pages drop all four.
 *
 * A car with no tickets in the window reads as em-dashes rather than zeroes — no visits
 * recorded is not the same claim as no visits made.
 */
export function ElevatorKpiFields({
  kpis,
  propertyId,
  loading,
  onTimeTarget,
}: {
  kpis: CarInProperty | null;
  propertyId: string | undefined;
  loading: boolean;
  /**
   * The share of callbacks this car's contract requires be answered in time, as a
   * fraction. `null` where the contract set none, which draws no verdict rather than a
   * mark against a target nobody agreed to.
   */
  onTimeTarget: number | null;
}) {
  const terms = useLocaleTerms();
  const loaded = !loading;

  const ticketsUrl = (entryType: EntryTypeKey[]) =>
    propertyId ? buildUrlForPropertyTickets({ propertyId, carId: kpis?.id, entryType }) : '';

  const visitsUrl = ticketsUrl(['visit']);
  const callbacksUrl = ticketsUrl(callbackTypeValues);
  const emergencyUrl = ticketsUrl(['emergency']);

  const maintenanceCompletion = kpis?.maintenanceCompletion;
  const callbackRate = kpis?.callbackRate;
  const emergencyCallbackRate = kpis?.emergencyCallbackRate;
  const onTimeResponse = kpis?.onTimeResponse;

  return (
    <FieldRow>
      <KpiField
        label="Maintenance visits"
        loaded={loaded}
        value={kpis?.maintenanceVisits != null ? formatNumber(kpis.maintenanceVisits) : null}
        href={visitsUrl}
      />

      {/* 100% is a real rule rather than a chosen threshold: the figure is already
          normalised against this elevator's own contracted quantity and capped at 1.0. */}
      <KpiField
        label="Maint. completion"
        loaded={loaded}
        value={maintenanceCompletion != null ? formatPercent(maintenanceCompletion) : null}
        href={visitsUrl}
        isGood={
          maintenanceCompletion != null
            ? maintenanceCompletion >= FULL_MAINTENANCE_COMPLETION
            : undefined
        }
      />

      <KpiField
        label="Maintenance hours"
        loaded={loaded}
        value={kpis?.maintenanceHours != null ? formatDecimalTwo(kpis.maintenanceHours) : null}
        href={visitsUrl}
      />

      <KpiField
        label={terms.callbacks}
        loaded={loaded}
        value={kpis?.callbacks != null ? formatNumber(kpis.callbacks) : null}
        href={callbacksUrl}
      />

      <KpiField
        label={terms.callbackRate}
        loaded={loaded}
        value={callbackRate != null ? formatDecimal(callbackRate) : null}
        href={callbacksUrl}
        isGood={
          callbackRate != null && kpis ? callbackRate <= kpis.regularCallbackRateTarget : undefined
        }
      />

      <KpiField
        label="Emergency calls"
        loaded={loaded}
        value={kpis?.emergencyCalls != null ? formatNumber(kpis.emergencyCalls) : null}
        href={emergencyUrl}
      />

      <KpiField
        label={terms.emergencyCallbackRate}
        loaded={loaded}
        value={emergencyCallbackRate != null ? formatDecimal(emergencyCallbackRate) : null}
        href={emergencyUrl}
        isGood={
          emergencyCallbackRate != null && kpis
            ? emergencyCallbackRate <= kpis.emergencyCallbackRateTarget
            : undefined
        }
      />

      <KpiField
        label="MTBC"
        loaded={loaded}
        value={
          kpis?.meanTimeBetweenCallbackSecs
            ? formatSecondsToDays(kpis.meanTimeBetweenCallbackSecs)
            : null
        }
        href={callbacksUrl}
      />

      {/* Judged against the contract's own target, and against nothing at all where it set
          none. This used to compare every elevator to a flat 80% that no contract has agreed
          to — see `_on_time_target_pct` in kpis/target_compliance.py. */}
      <KpiField
        label="On-time response"
        loaded={loaded}
        value={onTimeResponse != null ? formatPercent(Number(onTimeResponse)) : null}
        href={visitsUrl}
        isGood={
          onTimeResponse != null && onTimeTarget !== null
            ? Number(onTimeResponse) >= onTimeTarget
            : undefined
        }
      />
    </FieldRow>
  );
}
